import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ImageUpload } from '../../ui/image-upload';
import { Plus, Trash2, GripVertical, Type, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Link2, Copy, Check } from 'lucide-react';
import { applyMarkdownFormat } from './constants';
import { useState } from 'react';
import { slugifyAnchor, getSectionAnchor } from '../../../utils/anchor';

export const BlocksTab = ({ formData, setFormData, publicUrl }) => {
  const sections = formData.content_sections || [];
  const [copiedId, setCopiedId] = useState(null);

  const updateSections = (newSections) => setFormData({ ...formData, content_sections: newSections });

  const copySectionLink = (section) => {
    const anchor = getSectionAnchor(section);
    const link = publicUrl ? `${publicUrl}#${anchor}` : `#${anchor}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(section.id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const addSection = () => {
    updateSections([...sections, { id: crypto.randomUUID(), title: '', order: sections.length, blocks: [] }]);
  };

  const removeSection = (id) => updateSections(sections.filter(s => s.id !== id));

  const moveSection = (index, dir) => {
    const arr = [...sections];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    arr.forEach((s, i) => (s.order = i));
    updateSections(arr);
  };

  const updateSection = (id, patch) => updateSections(sections.map(s => s.id === id ? { ...s, ...patch } : s));

  const addBlock = (sectionId) => {
    const sec = sections.find(s => s.id === sectionId);
    if (!sec) return;
    const newBlock = {
      id: crypto.randomUUID(), columns: 1, order: sec.blocks?.length || 0,
      items: [{ id: crypto.randomUUID(), type: 'text', text: '', image: '', cta_text: '', cta_link: '' }],
    };
    updateSection(sectionId, { blocks: [...(sec.blocks || []), newBlock] });
  };

  const removeBlock = (sectionId, blockId) => {
    const sec = sections.find(s => s.id === sectionId);
    if (!sec) return;
    updateSection(sectionId, { blocks: sec.blocks.filter(b => b.id !== blockId) });
  };

  const moveBlock = (sectionId, index, dir) => {
    const sec = sections.find(s => s.id === sectionId);
    if (!sec) return;
    const blocks = [...sec.blocks];
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    blocks.forEach((b, i) => (b.order = i));
    updateSection(sectionId, { blocks });
  };

  const updateBlockItem = (sectionId, blockId, itemIndex, patch) => {
    const newSections = sections.map(s => {
      if (s.id !== sectionId) return s;
      return {
        ...s,
        blocks: s.blocks.map(b => {
          if (b.id !== blockId) return b;
          const items = [...b.items];
          items[itemIndex] = { ...items[itemIndex], ...patch };
          return { ...b, items };
        }),
      };
    });
    updateSections(newSections);
  };

  const changeColumns = (sectionId, blockId, newColumns) => {
    const newSections = sections.map(s => {
      if (s.id !== sectionId) return s;
      return {
        ...s,
        blocks: s.blocks.map(b => {
          if (b.id !== blockId) return b;
          let items = [...(b.items || [])];
          while (items.length < newColumns) items.push({ id: crypto.randomUUID(), type: 'text', text: '', image: '', cta_text: '', cta_link: '' });
          if (items.length > newColumns) items = items.slice(0, newColumns);
          return { ...b, columns: newColumns, items };
        }),
      };
    });
    updateSections(newSections);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <Label className="text-base">Sezioni Contenuto</Label>
          <p className="text-xs text-muted-foreground mt-1">Aggiungi sezioni con titolo e blocchi personalizzati</p>
        </div>
        <Button type="button" size="sm" onClick={addSection} data-testid="add-content-section-btn">
          <Plus size={16} className="mr-1" /> Aggiungi Sezione
        </Button>
      </div>

      <div className="space-y-6 max-h-[550px] overflow-y-auto pr-2">
        {sections.sort((a, b) => (a.order || 0) - (b.order || 0)).map((section, sIdx) => (
          <div key={section.id} className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-4" data-testid={`content-section-${sIdx}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <GripVertical size={16} className="text-muted-foreground cursor-grab flex-shrink-0" />
                <Input value={section.title || ''} onChange={(e) => updateSection(section.id, { title: e.target.value })} placeholder="Titolo sezione..." className="bg-white/5 border-white/10 font-medium" />
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button type="button" size="sm" variant="ghost" disabled={sIdx === 0} onClick={() => moveSection(sIdx, -1)}>↑</Button>
                <Button type="button" size="sm" variant="ghost" disabled={sIdx === sections.length - 1} onClick={() => moveSection(sIdx, 1)}>↓</Button>
                <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => removeSection(section.id)}><Trash2 size={14} /></Button>
              </div>
            </div>

            {/* Anchor URL for direct linking */}
            <div className="flex items-center gap-2 pl-7">
              <Link2 size={14} className="text-muted-foreground flex-shrink-0" />
              <Input
                value={section.anchor || ''}
                onChange={(e) => updateSection(section.id, { anchor: slugifyAnchor(e.target.value) })}
                placeholder={slugifyAnchor(section.title) || 'anchor-personalizzato'}
                className="bg-white/5 border-white/10 text-xs font-mono h-8"
                data-testid={`section-anchor-input-${sIdx}`}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 flex-shrink-0"
                onClick={() => copySectionLink(section)}
                title="Copia link diretto a questa sezione"
                data-testid={`copy-section-link-${sIdx}`}
              >
                {copiedId === section.id ? <><Check size={12} className="mr-1" />Copiato</> : <><Copy size={12} className="mr-1" />Copia link</>}
              </Button>
            </div>

            <div className="flex justify-end">
              <Button type="button" size="sm" variant="outline" onClick={() => addBlock(section.id)}>
                <Plus size={14} className="mr-1" /> Aggiungi Riga
              </Button>
            </div>

            <div className="space-y-4">
              {(section.blocks || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((block, bIdx) => (
                <div key={block.id} className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Riga {bIdx + 1}</span>
                      <Select value={String(block.columns || 1)} onValueChange={(v) => changeColumns(section.id, block.id, parseInt(v))}>
                        <SelectTrigger className="bg-white/5 border-white/10 w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 colonna</SelectItem>
                          <SelectItem value="2">2 colonne</SelectItem>
                          <SelectItem value="3">3 colonne</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={bIdx === 0} onClick={() => moveBlock(section.id, bIdx, -1)}>↑</Button>
                      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={bIdx === (section.blocks?.length || 0) - 1} onClick={() => moveBlock(section.id, bIdx, 1)}>↓</Button>
                      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive hover:text-destructive" onClick={() => removeBlock(section.id, block.id)}><Trash2 size={12} /></Button>
                    </div>
                  </div>

                  <div className={`grid gap-3 ${block.columns === 2 ? 'grid-cols-2' : block.columns === 3 ? 'grid-cols-3' : 'grid-cols-1'}`}>
                    {(block.items || []).map((item, iIdx) => (
                      <BlockItem key={item.id} item={item} itemIndex={iIdx} block={block} section={section} columns={block.columns} onUpdate={(patch) => updateBlockItem(section.id, block.id, iIdx, patch)} />
                    ))}
                  </div>
                </div>
              ))}

              {(!section.blocks || section.blocks.length === 0) && (
                <div className="text-center py-4 text-muted-foreground border border-dashed border-white/10 rounded-lg">
                  <p className="text-xs">Nessuna riga. Clicca "Aggiungi Riga"</p>
                </div>
              )}
            </div>
          </div>
        ))}

        {sections.length === 0 && (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-white/10 rounded-xl">
            <Type size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nessuna sezione aggiunta</p>
            <p className="text-xs mt-1">Clicca "Aggiungi Sezione" per creare contenuti personalizzati</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BlockItem = ({ item, itemIndex, block, section, columns, onUpdate }) => {
  const textareaId = `textarea-${item.type}-${section.id}-${block.id}-${itemIndex}`;

  const formatToolbar = (
    <div className="flex items-center gap-1 flex-wrap">
      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" title="Grassetto"
        onClick={() => applyMarkdownFormat(textareaId, 'bold', item.text || '', (t) => onUpdate({ text: t }))}>
        <Bold size={12} />
      </Button>
      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" title="Corsivo"
        onClick={() => applyMarkdownFormat(textareaId, 'italic', item.text || '', (t) => onUpdate({ text: t }))}>
        <Italic size={12} />
      </Button>
      <div className="w-px h-4 bg-white/20 mx-1" />
      {['left', 'center', 'right'].map((align) => {
        const Icon = align === 'left' ? AlignLeft : align === 'center' ? AlignCenter : AlignRight;
        return (
          <Button key={align} type="button" size="sm" variant={(item.textAlign || 'left') === align ? 'default' : 'ghost'} className="h-6 w-6 p-0"
            onClick={() => onUpdate({ textAlign: align })} title={`Allinea ${align === 'left' ? 'a sinistra' : align === 'center' ? 'al centro' : 'a destra'}`}>
            <Icon size={12} />
          </Button>
        );
      })}
      <div className="w-px h-4 bg-white/20 mx-1" />
      <Select value={item.fontSize || 'base'} onValueChange={(v) => onUpdate({ fontSize: v })}>
        <SelectTrigger className="bg-white/5 border-white/10 h-6 w-16 text-[10px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="sm">Piccolo</SelectItem>
          <SelectItem value="base">Normale</SelectItem>
          <SelectItem value="lg">Grande</SelectItem>
          <SelectItem value="xl">XL</SelectItem>
          <SelectItem value="2xl">2XL</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-2 rounded bg-white/5 border border-white/10 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground">{columns > 1 ? `Col ${itemIndex + 1}` : 'Contenuto'}</span>
        <Select value={item.type || 'text'} onValueChange={(v) => onUpdate({ type: v })}>
          <SelectTrigger className="bg-white/5 border-white/10 h-6 w-28 text-[10px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Testo</SelectItem>
            <SelectItem value="image">Immagine</SelectItem>
            <SelectItem value="cta">CTA</SelectItem>
            <SelectItem value="text_cta">Testo + CTA</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {item.type === 'text' && (
        <div className="space-y-2">
          {formatToolbar}
          <Textarea id={textareaId} value={item.text || ''} onChange={(e) => onUpdate({ text: e.target.value })} placeholder="Inserisci il testo... (usa **grassetto** o *corsivo*)" className="bg-white/5 border-white/10 min-h-16 text-xs font-mono" style={{ textAlign: item.textAlign || 'left' }} />
          <p className="text-[9px] text-muted-foreground">Seleziona il testo e clicca B o I per formattare</p>
        </div>
      )}

      {item.type === 'image' && (
        <div className="space-y-2">
          <ImageUpload value={item.image || ''} onChange={(url) => onUpdate({ image: url })} />
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">Altezza:</span>
            <Select value={item.imageHeight || '100'} onValueChange={(v) => onUpdate({ imageHeight: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 h-6 w-20 text-[10px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25%</SelectItem>
                <SelectItem value="50">50%</SelectItem>
                <SelectItem value="75">75%</SelectItem>
                <SelectItem value="100">100%</SelectItem>
                <SelectItem value="auto">Auto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {item.type === 'cta' && (
        <div className="space-y-1">
          <Input value={item.cta_text || ''} onChange={(e) => onUpdate({ cta_text: e.target.value })} placeholder="Testo pulsante..." className="bg-white/5 border-white/10 text-xs h-8" />
          <Input value={item.cta_link || ''} onChange={(e) => onUpdate({ cta_link: e.target.value })} placeholder="https://..." className="bg-white/5 border-white/10 text-xs h-8" />
        </div>
      )}

      {item.type === 'text_cta' && (
        <div className="space-y-2">
          {formatToolbar}
          <Textarea id={textareaId} value={item.text || ''} onChange={(e) => onUpdate({ text: e.target.value })} placeholder="Inserisci il testo... (usa **grassetto** o *corsivo*)" className="bg-white/5 border-white/10 min-h-16 text-xs font-mono" />
          <p className="text-[9px] text-muted-foreground">Seleziona il testo e clicca B o I per formattare</p>
          <div className="pt-2 border-t border-white/10 space-y-1">
            <span className="text-[10px] text-muted-foreground">Pulsante CTA</span>
            <Input value={item.cta_text || ''} onChange={(e) => onUpdate({ cta_text: e.target.value })} placeholder="Testo pulsante..." className="bg-white/5 border-white/10 text-xs h-8" />
            <Input value={item.cta_link || ''} onChange={(e) => onUpdate({ cta_link: e.target.value })} placeholder="https://..." className="bg-white/5 border-white/10 text-xs h-8" />
          </div>
        </div>
      )}
    </div>
  );
};
